import { createContext, useContext, useCallback, useEffect, useMemo, useState } from "react";

const ThemeContext = createContext({
  theme: "light",
  toggleTheme: () => {},
  setTheme: () => {},
});

const STORAGE_KEY = "abay-theme";

const systemPrefersDark = () =>
  typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;

// Stored value can be 'light' | 'dark' | 'system'. Legacy values ('light'/'dark')
// keep working. Default to 'system' when nothing is stored.
const getInitialMode = () => {
  if (typeof window === "undefined") return "system";
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark" || stored === "system") return stored;
  } catch (e) {
    void e;
  }
  return "system";
};

const resolveTheme = (mode) => {
  if (mode === "system") return systemPrefersDark() ? "dark" : "light";
  return mode === "dark" ? "dark" : "light";
};

const applyThemeClass = (theme) => {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (theme === "dark") {
    root.classList.add("dark");
    root.style.colorScheme = "dark";
  } else {
    root.classList.remove("dark");
    root.style.colorScheme = "light";
  }
};

export const ThemeProvider = ({ children }) => {
  const [mode, setModeState] = useState(getInitialMode);
  const [theme, setThemeResolved] = useState(() => resolveTheme(getInitialMode()));

  // Apply the resolved class before paint and persist the chosen mode.
  useEffect(() => {
    const resolved = resolveTheme(mode);
    setThemeResolved(resolved);
    applyThemeClass(resolved);
    try {
      window.localStorage.setItem(STORAGE_KEY, mode);
    } catch (e) {
      void e;
    }
  }, [mode]);

  // React to OS theme changes while in 'system' mode.
  useEffect(() => {
    if (mode !== "system" || typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => {
      const resolved = systemPrefersDark() ? "dark" : "light";
      setThemeResolved(resolved);
      applyThemeClass(resolved);
    };
    mq.addEventListener ? mq.addEventListener("change", onChange) : mq.addListener(onChange);
    return () => {
      mq.removeEventListener ? mq.removeEventListener("change", onChange) : mq.removeListener(onChange);
    };
  }, [mode]);

  const setMode = useCallback((next) => {
    setModeState(next === "dark" || next === "light" || next === "system" ? next : "system");
  }, []);

  // Back-compat: setTheme forces an explicit light/dark mode.
  const setTheme = useCallback((next) => {
    setModeState(next === "dark" ? "dark" : "light");
  }, []);

  const toggleTheme = useCallback(() => {
    setModeState((prev) => (resolveTheme(prev) === "dark" ? "light" : "dark"));
  }, []);

  const value = useMemo(
    () => ({ theme, isDark: theme === "dark", mode, setMode, toggleTheme, setTheme }),
    [theme, mode, setMode, toggleTheme, setTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => useContext(ThemeContext);

export default ThemeContext;
